import { RefreshCw } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Bars } from '../components/bars';
import { StatsUsage, type Usage } from '../components/stats-usage';
import { Card, btnGhost } from '../components/ui';
import { ApiError } from '../lib/api';
import { useSession } from '../lib/session';

interface DayStats {
  date: string;
  sessions: number;
  pageviews: number;
  visits: Record<string, number>;
  actions: Record<string, number>;
  topEvents: Array<{ slug: string; visits: number; id?: string; title?: string }>;
  eventActions: Array<{
    id: string;
    title?: string;
    add_calendar: number;
    share_event: number;
    save_event: number;
  }>;
  banners: Record<string, { impressions: number; clicks: number }>;
  refs: Record<string, number>;
  locales: Record<string, number>;
  shareChannels: Record<string, number>;
  filters?: Record<string, number>;
  /** per ora di Roma (24 voci), dal 4 ottobre 2026 */
  hours?: Array<{ pageviews: number; sessions: number; actions: number }>;
  /** visitatori per sessione (dal 15 ottobre 2026; ingresso e pagine per visita dal 4) */
  sources?: Record<string, { sessions: number; pageviews: number }>;
  devices?: Record<string, number>;
  countries?: Record<string, number>;
  landings?: Record<string, number>;
  depth?: Record<string, number>;
  /** uso del sito: sezioni ed elementi visti, funzioni e dettagli (components/stats-usage.tsx) */
  sections?: Record<string, number>;
  sectionItems?: Record<string, Record<string, number>>;
  features?: Record<string, number>;
  featureItems?: Record<string, Record<string, number>>;
  updatedAt?: string;
}
interface AiDay {
  date: string;
  costUsd: number;
  calls: number;
  byTask: Record<string, { costUsd: number; calls: number }>;
}
interface StatsResponse {
  from: string;
  to: string;
  days: DayStats[];
  ai: AiDay[];
}

const RANGES = [7, 30, 90] as const;
const PAGE_LABELS: Record<string, string> = {
  home: 'Home',
  event: 'Pagine evento',
  list: 'Elenco eventi',
  comune: 'Pagine comune',
  map: 'Mappa',
  saved: 'Salvati',
  services: 'Servizi',
  organizers: 'Organizzatori',
  guests: 'Ospiti di un luogo',
  page: 'Altre pagine',
};
const FILTER_LABELS: Record<string, string> = {
  period: 'Periodo (Oggi, Stasera, Weekend, date…)',
  type: 'Tipo di evento',
  city: 'Comune',
  search: 'Ricerca nell’elenco',
  near: 'Vicino a me',
  kids: 'Per famiglie',
  other: 'Altro',
};
const SOURCE_LABELS: Record<string, string> = {
  google: 'Google',
  'google-ads': 'Google Ads',
  bing: 'Bing',
  duckduckgo: 'DuckDuckGo',
  'altri-motori': 'Altri motori di ricerca',
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  x: 'X (Twitter)',
  youtube: 'YouTube',
  whatsapp: 'WhatsApp Web',
  telegram: 'Telegram',
  linkedin: 'LinkedIn',
  threads: 'Threads',
  qrcode: 'QR code',
  diretto: 'Diretto (indirizzo scritto, app, WhatsApp, preferiti)',
  interno: 'Dal sito stesso',
};
const DEVICE_LABELS: Record<string, string> = { m: 'Telefono', t: 'Tablet', d: 'Computer' };
const DEPTH_LABELS: Record<string, string> = {
  '1': '1 pagina',
  '2-3': '2 o 3 pagine',
  '4+': '4 pagine o più',
};
const regionName = (() => {
  const names = new Intl.DisplayNames(['it'], { type: 'region' });
  return (cc: string) => {
    try {
      return names.of(cc) ?? cc;
    } catch {
      return cc;
    }
  };
})();
const pct = (n: number, total: number) =>
  !total ? '—' : n && n / total < 0.005 ? '<1%' : `${Math.round((n / total) * 100)}%`;

const TASK_LABELS: Record<string, string> = {
  extract: 'Lettura dei testi',
  vision_ocr: 'Lettura delle locandine',
  dedup_judge: 'Controllo dei doppioni',
  enrich: 'Riscrittura dei testi',
  ig_caption: 'Didascalie Instagram',
  embed: 'Somiglianza (embedding)',
};

const nf = new Intl.NumberFormat('it-IT');
const usd = (n: number) =>
  new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(n);
const longDay = (d: string) =>
  new Intl.DateTimeFormat('it-IT', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(`${d}T12:00:00Z`));
const hourLabel = (h: string) => `${h.padStart(2, '0')}:00`;
/** 0 = lunedì */
const weekday = (d: string) => (new Date(`${d}T12:00:00Z`).getUTCDay() + 6) % 7;
const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const perSession = (pv: number, s: number) => (s ? (pv / s).toFixed(1).replace('.', ',') : '—');

const sumMaps = (maps: Array<Record<string, number>>) => {
  const out: Record<string, number> = {};
  for (const m of maps) for (const [k, v] of Object.entries(m ?? {})) out[k] = (out[k] ?? 0) + v;
  return out;
};
const sorted = (m: Record<string, number>) => Object.entries(m).sort((a, b) => b[1] - a[1]);

/**
 * Statistiche (docs/07-admin-e-bot.md §2, docs/08-analytics.md): visite e azioni dagli
 * aggregati giornalieri della Lambda `stats` (log di CloudFront, aggiornati ogni ora), eventi
 * più visti, banner, provenienza, e la spesa AI per giorno e per compito.
 */
export function Stats() {
  const { api, isAdmin } = useSession();
  const [days, setDays] = useState<(typeof RANGES)[number]>(30);
  const [data, setData] = useState<StatsResponse | null>(null);
  const [bannerNames, setBannerNames] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  /** giorno scelto per le visite per ora (null: tutto il periodo) */
  const [hourDay, setHourDay] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      setData(await api<StatsResponse>(`/stats?days=${days}`));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };
  useEffect(() => {
    setData(null);
    setHourDay(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);
  useEffect(() => {
    if (!isAdmin) return;
    api<{ banners: Array<{ id: string; alt: string }> }>('/config')
      .then((c) => setBannerNames(Object.fromEntries(c.banners.map((b) => [b.id, b.alt]))))
      .catch(() => {});
  }, [api, isAdmin]);

  const t = useMemo(() => {
    if (!data) return null;
    const ds = data.days;
    const events = new Map<string, { slug: string; id?: string; title?: string; visits: number }>();
    for (const d of ds)
      for (const e of d.topEvents ?? []) {
        const cur = events.get(e.slug) ?? { ...e, visits: 0 };
        cur.visits += e.visits;
        events.set(e.slug, cur);
      }
    const actionsBy = new Map<
      string,
      { id: string; title?: string; add_calendar: number; share_event: number; save_event: number }
    >();
    for (const d of ds)
      for (const e of d.eventActions ?? []) {
        const cur = actionsBy.get(e.id) ?? {
          id: e.id,
          ...(e.title ? { title: e.title } : {}),
          add_calendar: 0,
          share_event: 0,
          save_event: 0,
        };
        cur.add_calendar += e.add_calendar;
        cur.share_event += e.share_event;
        cur.save_event += e.save_event;
        actionsBy.set(e.id, cur);
      }
    const banners: Record<string, { impressions: number; clicks: number }> = {};
    for (const d of ds)
      for (const [id, b] of Object.entries(d.banners ?? {})) {
        banners[id] ??= { impressions: 0, clicks: 0 };
        banners[id].impressions += b.impressions;
        banners[id].clicks += b.clicks;
      }
    const aiTasks: Record<string, { costUsd: number; calls: number }> = {};
    for (const a of data.ai)
      for (const [k, v] of Object.entries(a.byTask)) {
        aiTasks[k] ??= { costUsd: 0, calls: 0 };
        aiTasks[k].costUsd += v.costUsd;
        aiTasks[k].calls += v.calls;
      }
    const sources: Record<string, { sessions: number; pageviews: number }> = {};
    for (const d of ds)
      for (const [k, v] of Object.entries(d.sources ?? {})) {
        sources[k] ??= { sessions: 0, pageviews: 0 };
        sources[k].sessions += v.sessions;
        sources[k].pageviews += v.pageviews;
      }
    const mergeNested = (list: Array<Record<string, Record<string, number>> | undefined>) => {
      const out: Record<string, Record<string, number>> = {};
      for (const m of list)
        for (const [k, v] of Object.entries(m ?? {})) out[k] = sumMaps([out[k] ?? {}, v]);
      return out;
    };
    const usage: Usage = {
      sections: sumMaps(ds.map((d) => d.sections ?? {})),
      sectionItems: mergeNested(ds.map((d) => d.sectionItems)),
      // i giorni senza `features` (prima del ricalcolo) hanno almeno le azioni principali
      features: sumMaps(ds.map((d) => d.features ?? d.actions ?? {})),
      featureItems: mergeNested(ds.map((d) => d.featureItems)),
    };
    return {
      usage,
      sources,
      devices: sumMaps(ds.map((d) => d.devices ?? {})),
      countries: sumMaps(ds.map((d) => d.countries ?? {})),
      landings: sumMaps(ds.map((d) => d.landings ?? {})),
      depth: sumMaps(ds.map((d) => d.depth ?? {})),
      pageviews: ds.reduce((n, d) => n + d.pageviews, 0),
      sessions: ds.reduce((n, d) => n + d.sessions, 0),
      visits: sumMaps(ds.map((d) => d.visits)),
      actions: sumMaps(ds.map((d) => d.actions)),
      refs: sumMaps(ds.map((d) => d.refs)),
      locales: sumMaps(ds.map((d) => d.locales)),
      channels: sumMaps(ds.map((d) => d.shareChannels)),
      filters: sumMaps(ds.map((d) => d.filters ?? {})),
      topEvents: [...events.values()].sort((a, b) => b.visits - a.visits).slice(0, 20),
      eventActions: [...actionsBy.values()]
        .sort(
          (a, b) =>
            b.add_calendar +
            b.share_event +
            b.save_event -
            (a.add_calendar + a.share_event + a.save_event),
        )
        .slice(0, 15),
      banners,
      aiTotal: data.ai.reduce((n, a) => n + a.costUsd, 0),
      aiTasks,
    };
  }, [data]);

  // serie completa del periodo (giorni senza dati a zero)
  const series = useMemo(() => {
    if (!data) return [];
    const byDate = new Map(data.days.map((d) => [d.date, d]));
    const aiBy = new Map(data.ai.map((a) => [a.date, a]));
    const out: Array<{ date: string; pageviews: number; sessions: number; ai: number }> = [];
    for (let d = data.from; d <= data.to;) {
      out.push({
        date: d,
        pageviews: byDate.get(d)?.pageviews ?? 0,
        sessions: byDate.get(d)?.sessions ?? 0,
        ai: aiBy.get(d)?.costUsd ?? 0,
      });
      const next = new Date(`${d}T12:00:00Z`);
      next.setUTCDate(next.getUTCDate() + 1);
      d = next.toISOString().slice(0, 10);
    }
    return out;
  }, [data]);

  // per ora: il giorno scelto o la somma dei giorni del periodo che hanno le ore
  const hourly = useMemo(() => {
    if (!data) return null;
    const withHours = data.days.filter((d) => d.hours?.length === 24);
    const picked = hourDay ? withHours.filter((d) => d.date === hourDay) : withHours;
    const hours = Array.from({ length: 24 }, () => ({ pageviews: 0, sessions: 0, actions: 0 }));
    // giorno della settimana × ora, pagine viste sui giorni del periodo
    const week = Array.from({ length: 7 }, () => Array<number>(24).fill(0));
    for (const d of withHours) {
      const w = weekday(d.date);
      d.hours!.forEach((h, i) => (week[w]![i]! += h.pageviews));
    }
    for (const d of picked)
      d.hours!.forEach((h, i) => {
        hours[i]!.pageviews += h.pageviews;
        hours[i]!.sessions += h.sessions;
        hours[i]!.actions += h.actions;
      });
    const peak = hours.reduce((best, h, i) => (h.pageviews > hours[best]!.pageviews ? i : best), 0);
    return {
      days: withHours.map((d) => d.date),
      hours,
      peak: hours[peak]!.pageviews ? peak : null,
      week,
      weekMax: Math.max(1, ...week.flat()),
      weekDays: [0, 1, 2, 3, 4, 5, 6].map(
        (w) => withHours.filter((d) => weekday(d.date) === w).length,
      ),
    };
  }, [data, hourDay]);

  // dettaglio per giorno, dal più recente
  const daily = useMemo(() => {
    if (!data) return [];
    const byDate = new Map(data.days.map((d) => [d.date, d]));
    return [...series].reverse().map((s) => ({ ...s, day: byDate.get(s.date) }));
  }, [data, series]);

  return (
    <main className="mx-auto max-w-[1200px] p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-h2">Statistiche</h1>
        <div className="flex items-center gap-1">
          {RANGES.map((r) => (
            <button
              key={r}
              aria-pressed={days === r}
              onClick={() => setDays(r)}
              className={`h-9 px-3 text-sm ${days === r ? 'bg-fg text-page' : 'bg-surface hover:bg-surface-strong'}`}
            >
              {r} giorni
            </button>
          ))}
          <button onClick={() => void load()} className={btnGhost} aria-label="Aggiorna">
            <RefreshCw className="size-4" aria-hidden />
          </button>
        </div>
      </div>
      <p className="mt-1 text-sm text-muted">
        Dati senza cookie né IP, aggiornati ogni ora. Le visite si contano dal giorno in cui sono
        attivi i log del sito.
      </p>
      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      {!data || !t ? (
        <p className="mt-4 text-sm text-muted">{error ? '' : 'Caricamento…'}</p>
      ) : (
        <div className="mt-4 flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Kpi label="Pagine viste" value={nf.format(t.pageviews)} />
            <Kpi label="Sessioni" value={nf.format(t.sessions)} />
            <Kpi label="Al calendario" value={nf.format(t.actions.add_calendar ?? 0)} />
            <Kpi label="Condivisioni" value={nf.format(t.actions.share_event ?? 0)} />
            <Kpi label="Spesa AI" value={usd(t.aiTotal)} />
          </div>

          <Card title="Visite per giorno">
            {t.pageviews === 0 ? (
              <p className="text-sm text-muted">Nessuna visita registrata nel periodo.</p>
            ) : (
              <Bars
                data={series.map((s) => ({ label: s.date, value: s.pageviews, extra: s.sessions }))}
                legend="pagine viste (barra) · sessioni (segno)"
                valueLabel="pagine viste"
              />
            )}
          </Card>

          {hourly && (
            <div className="grid gap-4 lg:grid-cols-2">
              <Card
                title="Visite per ora"
                actions={
                  <select
                    value={hourDay ?? ''}
                    onChange={(e) => setHourDay(e.target.value || null)}
                    className="h-8 border border-surface-strong bg-page px-2 text-sm"
                    aria-label="Giorno"
                  >
                    <option value="">Tutto il periodo</option>
                    {[...hourly.days].reverse().map((d) => (
                      <option key={d} value={d}>
                        {longDay(d)}
                      </option>
                    ))}
                  </select>
                }
              >
                {hourly.days.length === 0 ? (
                  <p className="text-sm text-muted">
                    Le visite per ora si contano dal 4 ottobre 2026.
                  </p>
                ) : (
                  <>
                    <Bars
                      data={hourly.hours.map((h, i) => ({
                        label: String(i),
                        value: h.pageviews,
                        extra: h.sessions,
                      }))}
                      formatLabel={hourLabel}
                      valueLabel="pagine viste"
                      legend={`pagine viste (barra) · sessioni (segno) · ora italiana${
                        hourDay ? '' : ` · somma di ${hourly.days.length} giorni`
                      }`}
                    />
                    {hourly.peak !== null && (
                      <p className="text-sm">
                        Ora con più visite: <strong>{hourLabel(String(hourly.peak))}</strong> (
                        {nf.format(hourly.hours[hourly.peak]!.pageviews)} pagine viste,{' '}
                        {nf.format(hourly.hours[hourly.peak]!.actions)} azioni sugli eventi)
                      </p>
                    )}
                  </>
                )}
              </Card>
              <Card title="Giorno della settimana e ora">
                {hourly.days.length === 0 ? (
                  <p className="text-sm text-muted">
                    Le visite per ora si contano dal 4 ottobre 2026.
                  </p>
                ) : (
                  <Heatmap week={hourly.week} max={hourly.weekMax} count={hourly.weekDays} />
                )}
              </Card>
            </div>
          )}

          <Visitors t={t} />

          <StatsUsage usage={t.usage} />

          <Card title="Dettaglio per giorno">
            <div className="max-h-[480px] overflow-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="sticky top-0 bg-page text-left text-[11px] uppercase tracking-[0.04em] text-muted">
                  <tr>
                    {[
                      'Giorno',
                      'Pagine viste',
                      'Sessioni',
                      'Pagine per sessione',
                      'Calendario',
                      'Condivisioni',
                      'Salvati',
                      'Evento più visto',
                    ].map((h, i) => (
                      <th
                        key={h}
                        className={`py-1 font-medium ${i && i < 7 ? 'text-right' : ''} ${i ? 'pl-3' : ''}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {daily.map(({ date, pageviews, sessions, day }) => {
                    const top = day?.topEvents?.[0];
                    const hasHours = day?.hours?.length === 24;
                    return (
                      <tr
                        key={date}
                        className={`border-t border-surface-strong ${hourDay === date ? 'bg-accent-soft' : ''}`}
                      >
                        <td className="py-1.5 whitespace-nowrap">
                          {hasHours ? (
                            <button
                              onClick={() => setHourDay(hourDay === date ? null : date)}
                              className="hover:underline"
                              title="Visite per ora di questo giorno"
                            >
                              {longDay(date)}
                            </button>
                          ) : (
                            longDay(date)
                          )}
                        </td>
                        <td className="py-1.5 pl-3 text-right tabular-nums">
                          {nf.format(pageviews)}
                        </td>
                        <td className="py-1.5 pl-3 text-right tabular-nums">
                          {nf.format(sessions)}
                        </td>
                        <td className="py-1.5 pl-3 text-right tabular-nums">
                          {perSession(pageviews, sessions)}
                        </td>
                        <td className="py-1.5 pl-3 text-right tabular-nums">
                          {nf.format(day?.actions?.add_calendar ?? 0)}
                        </td>
                        <td className="py-1.5 pl-3 text-right tabular-nums">
                          {nf.format(day?.actions?.share_event ?? 0)}
                        </td>
                        <td className="py-1.5 pl-3 text-right tabular-nums">
                          {nf.format(day?.actions?.save_event ?? 0)}
                        </td>
                        <td className="max-w-[260px] truncate py-1.5 pl-3">
                          {top ? (
                            top.id ? (
                              <Link to={`/events/${top.id}`} className="hover:underline">
                                {top.title ?? top.slug}
                              </Link>
                            ) : (
                              top.slug
                            )
                          ) : (
                            '—'
                          )}
                          {top && <span className="text-muted"> · {nf.format(top.visits)}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted">
              Clic sul giorno per vedere le visite per ora (dal 4 ottobre 2026).
            </p>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Eventi più visti">
              {t.topEvents.length === 0 ? (
                <Empty />
              ) : (
                <Table
                  head={['Evento', 'Visite']}
                  rows={t.topEvents.map((e) => [
                    e.id ? (
                      <Link to={`/events/${e.id}`} className="hover:underline">
                        {e.title ?? e.slug}
                      </Link>
                    ) : (
                      e.slug
                    ),
                    nf.format(e.visits),
                  ])}
                />
              )}
            </Card>
            <Card title="Eventi con più azioni">
              {t.eventActions.length === 0 ? (
                <Empty />
              ) : (
                <Table
                  head={['Evento', 'Calendario', 'Condivisioni', 'Salvati']}
                  rows={t.eventActions.map((e) => [
                    <Link key={e.id} to={`/events/${e.id}`} className="hover:underline">
                      {e.title ?? e.id}
                    </Link>,
                    nf.format(e.add_calendar),
                    nf.format(e.share_event),
                    nf.format(e.save_event),
                  ])}
                />
              )}
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Filtri dell’elenco">
              <Table
                head={['Filtro', 'Volte']}
                rows={
                  Object.keys(t.filters).length
                    ? sorted(t.filters).map(([k, v]) => [FILTER_LABELS[k] ?? k, nf.format(v)])
                    : [['Nessun dato (conteggio dal 4 ottobre 2026)', '']]
                }
              />
            </Card>
            <Card title="Lingua, link marcati e condivisioni">
              <Table
                head={['Link marcato (ref)', 'Viste']}
                rows={
                  Object.keys(t.refs).length
                    ? sorted(t.refs).map(([k, v]) => [k, nf.format(v)])
                    : [['diretta o motori di ricerca', nf.format(t.pageviews)]]
                }
              />
              <p className="text-xs text-muted">
                Lingue:{' '}
                {sorted(t.locales)
                  .map(([k, v]) => `${k} ${Math.round((v / Math.max(1, t.pageviews)) * 100)}%`)
                  .join(' · ') || '—'}
              </p>
              {Object.keys(t.channels).length > 0 && (
                <p className="text-xs text-muted">
                  Condivisioni:{' '}
                  {sorted(t.channels)
                    .map(([k, v]) => `${k} ${nf.format(v)}`)
                    .join(' · ')}
                </p>
              )}
            </Card>
          </div>

          <Card title="Banner propri">
            {Object.keys(t.banners).length === 0 ? (
              <p className="text-sm text-muted">Nessun banner mostrato nel periodo.</p>
            ) : (
              <Table
                head={['Banner', 'Impression', 'Click', 'CTR']}
                rows={Object.entries(t.banners)
                  .sort((a, b) => b[1].impressions - a[1].impressions)
                  .map(([id, b]) => [
                    bannerNames[id] ?? id,
                    nf.format(b.impressions),
                    nf.format(b.clicks),
                    b.impressions
                      ? `${((b.clicks / b.impressions) * 100).toFixed(1).replace('.', ',')}%`
                      : '—',
                  ])}
              />
            )}
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Spesa AI per giorno">
              <Bars
                data={series.map((s) => ({ label: s.date, value: s.ai }))}
                format={usd}
                legend="dollari stimati dai prezzi dei modelli"
              />
            </Card>
            <Card title="Spesa AI per compito">
              {Object.keys(t.aiTasks).length === 0 ? (
                <p className="text-sm text-muted">
                  La spesa per compito si registra dai prossimi utilizzi dell’AI.
                </p>
              ) : (
                <Table
                  head={['Compito', 'Chiamate', 'Spesa']}
                  rows={Object.entries(t.aiTasks)
                    .sort((a, b) => b[1].costUsd - a[1].costUsd)
                    .map(([k, v]) => [TASK_LABELS[k] ?? k, nf.format(v.calls), usd(v.costUsd)])}
                />
              )}
            </Card>
          </div>
        </div>
      )}
    </main>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-surface-strong bg-page p-3">
      <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted">{label}</p>
      <p className="mt-1 font-display text-h2">{value}</p>
    </div>
  );
}

/** Da dove arrivano, con che dispositivo, da quale paese, dove entrano e quanto restano. */
function Visitors({
  t,
}: {
  t: {
    sources: Record<string, { sessions: number; pageviews: number }>;
    devices: Record<string, number>;
    countries: Record<string, number>;
    landings: Record<string, number>;
    depth: Record<string, number>;
  };
}) {
  const total = (m: Record<string, number>) => Object.values(m).reduce((a, b) => a + b, 0);
  const since = (what: string) => (
    <p className="text-sm text-muted">{what} si conta dal 15 ottobre 2026.</p>
  );
  const srcTotal = Object.values(t.sources).reduce((n, v) => n + v.sessions, 0);
  const countries = sorted(t.countries);
  const shown = countries.slice(0, 12);
  const rest = countries.slice(12).reduce((n, [, v]) => n + v, 0);
  return (
    <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
      <Card title="Da dove arrivano">
        {srcTotal === 0 ? (
          since('La provenienza')
        ) : (
          <>
            <Table
              head={['Provenienza', 'Visite', 'Quota', 'Pagine/visita']}
              rows={Object.entries(t.sources)
                .sort((a, b) => b[1].sessions - a[1].sessions)
                .slice(0, 15)
                .map(([k, v]) => [
                  SOURCE_LABELS[k] ?? k,
                  nf.format(v.sessions),
                  pct(v.sessions, srcTotal),
                  perSession(v.pageviews, v.sessions),
                ])}
            />
            <p className="text-xs text-muted">
              Una visita è una sessione: conta la prima pagina vista. Dai link con utm_source o ref
              vale il loro nome; WhatsApp e molte app non dicono da dove si arriva e finiscono in
              «Diretto».
            </p>
          </>
        )}
      </Card>
      <div className="grid gap-4 sm:grid-cols-2">
        <Card title="Dispositivo">
          {total(t.devices) === 0 ? (
            since('Il dispositivo')
          ) : (
            <Table
              head={['', 'Quota']}
              rows={sorted(t.devices).map(([k, v]) => [
                DEVICE_LABELS[k] ?? k,
                `${pct(v, total(t.devices))} · ${nf.format(v)}`,
              ])}
            />
          )}
        </Card>
        <Card title="Pagine per visita">
          {total(t.depth) === 0 ? (
            <Empty />
          ) : (
            <Table
              head={['', 'Quota']}
              rows={['1', '2-3', '4+'].map((k) => [
                DEPTH_LABELS[k]!,
                `${pct(t.depth[k] ?? 0, total(t.depth))} · ${nf.format(t.depth[k] ?? 0)}`,
              ])}
            />
          )}
        </Card>
        <Card title="Paese">
          {total(t.countries) === 0 ? (
            since('Il paese')
          ) : (
            <Table
              head={['', 'Quota']}
              rows={[
                ...shown.map(([k, v]) => [regionName(k), pct(v, total(t.countries))]),
                ...(rest ? [['Altri', pct(rest, total(t.countries))]] : []),
              ]}
            />
          )}
        </Card>
        <Card title="Pagina d’ingresso">
          {total(t.landings) === 0 ? (
            <Empty />
          ) : (
            <Table
              head={['', 'Quota']}
              rows={sorted(t.landings).map(([k, v]) => [
                PAGE_LABELS[k] ?? k,
                pct(v, total(t.landings)),
              ])}
            />
          )}
        </Card>
      </div>
    </div>
  );
}

/** Pagine viste per giorno della settimana e ora: più scuro, più visite. */
function Heatmap({ week, max, count }: { week: number[][]; max: number; count: number[] }) {
  return (
    <figure>
      <div className="overflow-x-auto">
        <div
          className="grid min-w-[480px] gap-px text-[10px] text-muted"
          style={{ gridTemplateColumns: '2.5rem repeat(24, minmax(0, 1fr))' }}
        >
          <span />
          {Array.from({ length: 24 }, (_, h) => (
            <span key={h} className="text-center tabular-nums">
              {h % 3 === 0 ? h : ''}
            </span>
          ))}
          {week.map((row, w) => (
            <Fragment key={w}>
              <span className="pr-1 leading-5">{WEEKDAYS[w]}</span>
              {row.map((v, h) => (
                <span
                  key={h}
                  className="h-5 bg-surface"
                  title={`${WEEKDAYS[w]} ${hourLabel(String(h))}: ${nf.format(v)} pagine viste in ${count[w]} giorni`}
                >
                  <span
                    className="block h-full bg-fg"
                    style={{ opacity: v ? 0.12 + (v / max) * 0.88 : 0 }}
                  />
                </span>
              ))}
            </Fragment>
          ))}
        </div>
      </div>
      <figcaption className="mt-2 text-xs text-muted">
        Pagine viste per giorno della settimana e ora italiana, sommate sui giorni del periodo
        (passa sopra un quadratino per il numero). Utile per scegliere quando pubblicare.
      </figcaption>
    </figure>
  );
}

const Empty = () => <p className="text-sm text-muted">Nessun dato nel periodo.</p>;

function Table({ head, rows }: { head: string[]; rows: ReactNode[][] }) {
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-[11px] uppercase tracking-[0.04em] text-muted">
        <tr>
          {head.map((h, i) => (
            <th key={h} className={`py-1 font-medium ${i ? 'pl-3 text-right' : ''}`}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-surface-strong">
            {r.map((c, j) => (
              <td key={j} className={`py-1.5 ${j ? 'pl-3 text-right tabular-nums' : 'pr-3'}`}>
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
